import React, { useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Play,
  User,
  Search,
  Sparkles,
  TrendingUp,
  CheckCircle2,
  Clock3,
  Compass,
  ArrowRight,
  Award,
  Trophy,
  Target,
  Flame,
  Lock,
  Plus,
  HelpCircle,
} from 'lucide-react';

import {
  useGetMeQuery,
  useGetMyCoursesQuery,
  useGetPublicCoursesQuery,
  useGetMyQuizzesQuery,
  useGetMyCertificatesQuery,
} from '../../api/apiSlice';

import CourseCard from '../../components/course/CourseCard';
import { COURSE_COVER_FALLBACK_SM, setFallback } from '../../lib/placeholders';

function pickGreeting(t) {
  const h = new Date().getHours();

  if (h < 12) return t('studentDashboard.greetingMorning');
  if (h < 18) return t('studentDashboard.greetingDay');

  return t('studentDashboard.greetingEvening');
}

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: user } = useGetMeQuery();

  const {
    data: enrollments = [],
    isLoading: isEnrollmentsLoading,
    isSuccess: isEnrollmentsLoaded,
  } = useGetMyCoursesQuery();

  const {
    data: catalog,
    isLoading: isCatalogLoading,
    isSuccess: isCatalogLoaded,
  } = useGetPublicCoursesQuery({
    page: 1,
    limit: 6,
  });

  const {
    data: myQuizzes,
    isLoading: isQuizzesLoading,
    isSuccess: isQuizzesLoaded,
  } = useGetMyQuizzesQuery(undefined, {
    skip: !user,
  });

  // Сертификаты студента — показываем preview-секцию если есть хоть один.
  // Один запрос на дашборд, дальше кэш RTK Query.
  const { data: certificatesData } = useGetMyCertificatesQuery(undefined, {
    skip: !user,
  });
  const certificates = certificatesData?.items || [];

  const stats = useMemo(() => {
    const total = enrollments.length;

    const completed = enrollments.filter(
      (e) => (e.progress || 0) >= 100
    ).length;

    const avg = total
      ? Math.round(
          enrollments.reduce(
            (s, e) => s + (e.progress || 0),
            0
          ) / total
        )
      : 0;

    const completedLessons = enrollments.reduce(
      (s, e) =>
        s +
        (e.completedCount ??
          e.completedLessons?.length ??
          0),
      0
    );

    return {
      total,
      completed,
      avg,
      completedLessons,
    };
  }, [enrollments]);

  const continueCourse = useMemo(() => {
    if (!enrollments.length) return null;

    const sorted = [...enrollments].sort(
      (a, b) =>
        new Date(
          b.lastAccess ||
            b.purchaseDate ||
            0
        ) -
        new Date(
          a.lastAccess ||
            a.purchaseDate ||
            0
        )
    );

    return (
      sorted.find(
        (e) => (e.progress || 0) < 100
      ) || sorted[0]
    );
  }, [enrollments]);

  const recent = useMemo(() => {
    return [...enrollments]
      .sort(
        (a, b) =>
          new Date(
            b.lastAccess ||
              b.purchaseDate ||
              0
          ) -
          new Date(
            a.lastAccess ||
              a.purchaseDate ||
              0
          )
      )
      .slice(0, 6);
  }, [enrollments]);

  const recommended = useMemo(() => {
    const owned = new Set(
      enrollments.map((e) =>
        String(e.course?._id)
      )
    );

    return (catalog?.items || [])
      .filter(
        (c) => !owned.has(String(c._id))
      )
      .slice(0, 3);
  }, [catalog, enrollments]);

  const achievements = useMemo(() => {
    return [
      {
        key: 'firstPurchase',
        icon: Award,
        unlocked: stats.total >= 1,
        labelKey:
          'studentDashboard.achFirstPurchase',
      },
      {
        key: 'firstComplete',
        icon: Trophy,
        unlocked: stats.completed >= 1,
        labelKey:
          'studentDashboard.achFirstComplete',
      },
      {
        key: 'fiveCourses',
        icon: Target,
        unlocked: stats.total >= 5,
        labelKey:
          'studentDashboard.achFiveCourses',
      },
      {
        key: 'lessons10',
        icon: Flame,
        unlocked:
          stats.completedLessons >= 10,
        labelKey:
          'studentDashboard.achLessons10',
      },
    ];
  }, [stats]);

  const greeting = pickGreeting(t);

  const firstName =
    (user?.name || '').split(' ')[0] ||
    user?.email?.split('@')[0] ||
    '';

  return (
    <div className="relative min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* HERO — чистая slate-карточка с тонким акцентом, без неонового
            фиолета. Greeting слева, CTA-кнопки справа на десктопе. */}
        <header className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{greeting}</p>
              <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 dark:text-white">
                {firstName || t('roles.student')}
                <span className="ml-2">👋</span>
              </h1>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-xl">
                {t('studentDashboard.subtitle')}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm shadow-sm transition"
              >
                <Compass className="w-4 h-4" />
                {t('studentDashboard.quickFind')}
              </Link>
              <Link
                to="/my-courses"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition"
              >
                <BookOpen className="w-4 h-4" />
                {t('studentDashboard.allMyCourses')}
              </Link>
            </div>
          </div>
        </header>

        {/* CONTINUE */}
        {isEnrollmentsLoading ? (
          <ContinueSkeleton />
        ) : (
          continueCourse?.course && (
            <section className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              {/* На lg+ обложка слева 280px шириной + всегда aspect-video,
                  не растягивается на высоту правой колонки. Раньше lg:aspect-auto
                  делал картинку 380×N где N=высота текста → object-cover агрессивно
                  обрезал → user видел только треть изображения. */}
              <div className="grid lg:grid-cols-[280px_1fr]">
                <button
                  onClick={() =>
                    navigate(
                      `/learn/${continueCourse.course._id}`
                    )
                  }
                  className="group relative overflow-hidden aspect-video bg-gray-100 dark:bg-gray-800"
                >
                  {/* Backdrop blur fill + object-contain — обложка целиком,
                      поля заполнены размытой копией. Видно полную картинку
                      даже если её ratio не 16:9. */}
                  {continueCourse.course.coverImageUrl && (
                    <img
                      src={continueCourse.course.coverImageUrl}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-50 dark:opacity-30"
                    />
                  )}
                  <img
                    src={
                      continueCourse.course
                        .coverImageUrl ||
                      COURSE_COVER_FALLBACK_SM
                    }
                    alt={
                      continueCourse.course
                        .title
                    }
                    loading="lazy"
                    onError={setFallback(
                      COURSE_COVER_FALLBACK_SM
                    )}
                    className="absolute inset-0 h-full w-full object-contain transition duration-500 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="rounded-full bg-white/90 p-5 shadow-2xl backdrop-blur-xl transition group-hover:scale-110">
                      <Play className="h-8 w-8 fill-indigo-700 text-indigo-700" />
                    </div>
                  </div>

                  <div className="absolute left-6 bottom-6">
                    <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-xl px-4 py-2 text-white text-sm font-semibold">
                      <Clock3 className="w-4 h-4" />
                      {Math.round(
                        continueCourse.progress ||
                          0
                      )}
                      %
                    </div>
                  </div>
                </button>

                <div className="flex flex-col p-5 sm:p-6 lg:p-8">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                    {t('studentDashboard.continueHeading')}
                  </p>
                  <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
                    {continueCourse.course.title}
                  </h2>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    {continueCourse.course.instructor?.name || t('course.instructor')}
                  </p>

                  <div className="mt-6">
                    <div className="mb-2 flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">
                        {t('studentDashboard.yourProgress')}
                      </span>
                      <span className="font-semibold text-slate-900 dark:text-white tabular-nums">
                        {Math.round(continueCourse.progress || 0)}%
                      </span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                      <div
                        className="h-full rounded-full bg-indigo-600 transition-all"
                        style={{ width: `${continueCourse.progress || 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="mt-auto pt-6 flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/learn/${continueCourse.course._id}`)}
                      className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm shadow-sm transition"
                    >
                      <Play className="w-4 h-4" />
                      {t('studentDashboard.continueCta')}
                    </button>
                    <Link
                      to="/my-courses"
                      className="inline-flex items-center gap-2 h-11 px-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                    >
                      <BookOpen className="w-4 h-4" />
                      {t('studentDashboard.allMyCourses')}
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          )
        )}

        {/* KPI — каждая карточка со своим семантическим цветом:
            синий = курсы (нейтральный счётчик), зелёный = завершено (успех),
            индиго = средний прогресс (главная метрика), янтарный = время. */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={BookOpen} label={t('studentDashboard.kpiCourses')} value={stats.total} loading={isEnrollmentsLoading} color="blue" />
          <Kpi icon={CheckCircle2} label={t('studentDashboard.kpiCompleted')} value={stats.completed} loading={isEnrollmentsLoading} color="emerald" />
          <Kpi icon={TrendingUp} label={t('studentDashboard.kpiAvg')} value={`${stats.avg}%`} loading={isEnrollmentsLoading} color="indigo" />
          <Kpi icon={Clock3} label={t('studentDashboard.kpiLessons')} value={stats.completedLessons} loading={isEnrollmentsLoading} color="amber" />
        </section>

        {/* QUICK ACTIONS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <QuickAction
            to="/courses"
            icon={Compass}
            label={t(
              'studentDashboard.quickFind'
            )}
            sub={t(
              'studentDashboard.quickFindDesc'
            )}
          />

          <QuickAction
            to="/dashboard/student/quizzes"
            icon={HelpCircle}
            label={t(
              'studentDashboard.quickQuizzes'
            )}
            sub={t(
              'studentDashboard.quickQuizzesDesc'
            )}
          />

          <QuickAction
            to="/profile"
            icon={User}
            label={t(
              'studentDashboard.quickProfile'
            )}
            sub={t(
              'studentDashboard.quickProfileDesc'
            )}
          />
        </section>

        {/* RECENT */}
        <section className="space-y-5">
          <SectionHeader
            title={t(
              'studentDashboard.recentTitle'
            )}
            to="/my-courses"
            t={t}
          />

          {isEnrollmentsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {Array.from({
                length: 3,
              }).map((_, i) => (
                <CourseCardSkeleton
                  key={i}
                />
              ))}
            </div>
          ) : recent.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {recent.map((en) => (
                <RecentCard
                  key={en._id}
                  enrollment={en}
                  navigate={navigate}
                  t={t}
                />
              ))}
            </div>
          ) : (
            <EmptyState
              icon={Search}
              text={t(
                'studentDashboard.recentEmpty'
              )}
              ctaTo="/courses"
              ctaLabel={t(
                'studentDashboard.recentEmptyCta'
              )}
            />
          )}
        </section>

        {/* CERTIFICATES PREVIEW — отображается только если есть выпущенные.
            Показываем до 3 последних с CTA «Все сертификаты». */}
        {certificates.length > 0 && (
          <section className="space-y-5">
            <SectionHeader
              title={t('studentDashboard.certificatesTitle')}
              subtitle={t('studentDashboard.certificatesSubtitle')}
              to="/my-certificates"
              t={t}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {certificates.slice(0, 3).map((c) => (
                <Link
                  key={c._id}
                  to="/my-certificates"
                  className="group flex flex-col overflow-hidden rounded-xl border border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-950/20 p-4 transition hover:border-amber-300 dark:hover:border-amber-800/60"
                >
                  <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400">
                    <Award className="w-5 h-5" />
                  </div>
                  <h3 className="line-clamp-2 text-base font-bold text-gray-900 dark:text-white break-words">
                    {c.courseTitle}
                  </h3>
                  <div className="mt-1 font-mono text-xs text-amber-700 dark:text-amber-400">
                    {c.certificateNumber}
                  </div>
                  <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                    {new Date(c.issuedAt).toLocaleDateString(undefined, {
                      year: 'numeric', month: 'long', day: 'numeric',
                    })}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ACHIEVEMENTS */}
        <section className="space-y-5">
          <SectionTitle
            title={t(
              'studentDashboard.achievementsTitle'
            )}
          />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {achievements.map((a) => (
              <AchievementBadge
                key={a.key}
                {...a}
                t={t}
              />
            ))}
          </div>
        </section>

        {/* QUIZZES */}
        {isQuizzesLoading ? (
          <QuizzesSkeleton />
        ) : (
          isQuizzesLoaded &&
          Array.isArray(myQuizzes?.items) &&
          myQuizzes.items.length >
            0 && (
            <section className="space-y-5">
              <SectionHeader
                title={t(
                  'studentDashboard.quizzesTitle'
                )}
                subtitle={t(
                  'studentDashboard.quizzesSubtitle'
                )}
                to="/dashboard/student/quizzes"
                t={t}
              />

              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                    <HelpCircle className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold text-slate-900 dark:text-white tabular-nums">
                      {myQuizzes.items.length} / {myQuizzes.limit || 2}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {t('studentDashboard.quizzesSubtitle')}
                    </div>
                  </div>
                  <Link
                    to="/dashboard/student/quizzes/create"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm shadow-sm transition"
                  >
                    <Plus className="w-4 h-4" />
                    {t('studentDashboard.quizzesCreate')}
                  </Link>
                </div>
              </div>
            </section>
          )
        )}

        {/* DISCOVER */}
        {isCatalogLoading ? (
          <DiscoverSkeleton />
        ) : (
          isCatalogLoaded &&
          recommended.length > 0 && (
            <section className="space-y-5">
              <SectionHeader
                title={t(
                  'studentDashboard.discoverTitle'
                )}
                subtitle={t(
                  'studentDashboard.discoverSubtitle'
                )}
                to="/courses"
                t={t}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                {recommended.map((c) => (
                  <CourseCard
                    key={c._id}
                    course={c}
                  />
                ))}
              </div>
            </section>
          )
        )}
      </div>
    </div>
  );
};

/* COMPONENTS */

const KPI_COLORS = {
  blue:    { bg: 'bg-blue-50 dark:bg-blue-500/10',       fg: 'text-blue-600 dark:text-blue-400' },
  emerald: { bg: 'bg-emerald-50 dark:bg-emerald-500/10', fg: 'text-emerald-600 dark:text-emerald-400' },
  indigo:  { bg: 'bg-indigo-50 dark:bg-indigo-500/10',   fg: 'text-indigo-600 dark:text-indigo-400' },
  amber:   { bg: 'bg-amber-50 dark:bg-amber-500/10',     fg: 'text-amber-600 dark:text-amber-400' },
};

const Kpi = ({ icon: Icon, label, value, loading, color = 'indigo' }) => {
  const c = KPI_COLORS[color] || KPI_COLORS.indigo;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 transition hover:border-slate-300 dark:hover:border-slate-700">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 truncate">{label}</div>
        <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${c.bg} ${c.fg}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white tabular-nums">
        {loading ? '—' : value}
      </div>
    </div>
  );
};

const QuickAction = ({ to, icon: Icon, label, sub }) => (
  <Link
    to={to}
    className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 transition hover:border-indigo-300 dark:hover:border-indigo-700"
  >
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 dark:text-white text-sm">{label}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{sub}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition" />
    </div>
  </Link>
);

const RecentCard = ({ enrollment, navigate, t }) => {
  const course = enrollment.course || {};
  const progress = enrollment.progress || 0;
  const completed = !!enrollment.completedAt || progress >= 100;
  const lastAt = enrollment.lastAccess || enrollment.purchaseDate;

  return (
    <button
      onClick={() => navigate(`/learn/${course._id}`)}
      className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 transition hover:border-slate-300 dark:hover:border-slate-700 text-left"
    >
      {/* Cover — фиксированный 16:9 + blur-backdrop + object-contain.
          Видно полную обложку любых пропорций, карточки в сетке одной высоты. */}
      <div className="relative aspect-video overflow-hidden bg-gray-100 dark:bg-gray-800">
        {course.coverImageUrl && (
          <img
            src={course.coverImageUrl}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-50 dark:opacity-30"
          />
        )}
        <img
          src={course.coverImageUrl || COURSE_COVER_FALLBACK_SM}
          alt={course.title}
          loading="lazy"
          onError={setFallback(COURSE_COVER_FALLBACK_SM)}
          className="absolute inset-0 h-full w-full object-contain transition duration-500 group-hover:scale-105"
        />
        {completed && (
          <div className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[11px] font-bold text-white shadow-md">
            <CheckCircle2 className="w-3 h-3" />
            {t('studentDashboard.completedBadge')}
          </div>
        )}
      </div>

      {/* Content row */}
      <div className="flex flex-1 flex-col p-5">
        <h3 className="line-clamp-2 text-base font-bold text-gray-900 dark:text-white break-words">
          {course.title}
        </h3>
        <p className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">
          {course.instructor?.name || t('course.instructor')}
        </p>

        {/* Progress */}
        <div className="mt-4">
          <div className="mb-1 flex items-center justify-between text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
            <span>{Math.round(progress)}%</span>
            {lastAt && (
              <span>{new Date(lastAt).toLocaleDateString(undefined, { day: '2-digit', month: 'short' })}</span>
            )}
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                completed ? 'bg-emerald-500' : 'bg-indigo-600'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </button>
  );
};

const AchievementBadge = ({
  icon: Icon,
  unlocked,
  labelKey,
  t,
}) => (
  <div
    className={`rounded-xl border p-4 text-center transition ${
      unlocked
        ? 'border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300'
        : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-400 dark:text-slate-600'
    }`}
  >
    <div className="flex justify-center">
      <div className={`flex h-12 w-12 items-center justify-center rounded-lg ${
        unlocked ? 'bg-amber-100 dark:bg-amber-500/15' : 'bg-slate-100 dark:bg-slate-800'
      }`}>
        {unlocked ? <Icon className="w-6 h-6" /> : <Lock className="w-4 h-4" />}
      </div>
    </div>
    <div className="mt-3 text-xs font-semibold leading-snug">{t(labelKey)}</div>
  </div>
);

const SectionTitle = ({
  title,
}) => (
  <h2 className="text-2xl font-black tracking-tight text-gray-900 dark:text-white">
    {title}
  </h2>
);

const SectionHeader = ({ title, subtitle, to, t }) => (
  <div className="flex items-end justify-between gap-4">
    <div>
      <h2 className="text-2xl font-black tracking-tight text-gray-900 dark:text-white">
        {title}
      </h2>

      {subtitle && (
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {subtitle}
        </p>
      )}
    </div>

    {to && (
      <Link
        to={to}
        className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:underline"
      >
        {t ? t('studentDashboard.viewAll') : 'Все'}
        <ArrowRight className="w-4 h-4" />
      </Link>
    )}
  </div>
);

const ContinueSkeleton = () => (
  <div className="h-[320px] rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
);

const CourseCardSkeleton = () => (
  <div className="overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse">
    <div className="aspect-video bg-gray-200 dark:bg-gray-800" />

    <div className="p-5 space-y-3">
      <div className="h-5 w-2/3 rounded bg-gray-200 dark:bg-gray-800" />

      <div className="h-2 w-full rounded bg-gray-200 dark:bg-gray-800" />
    </div>
  </div>
);

const QuizzesSkeleton = () => (
  <div className="h-36 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
);

const DiscoverSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
    {Array.from({ length: 3 }).map(
      (_, i) => (
        <CourseCardSkeleton
          key={i}
        />
      )
    )}
  </div>
);

const EmptyState = ({
  icon: Icon,
  text,
  ctaTo,
  ctaLabel,
}) => (
  <div className="rounded-[32px] border border-dashed border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl p-12 text-center">
    <Icon className="w-14 h-14 mx-auto text-gray-400" />

    <p className="mt-4 text-gray-600 dark:text-gray-400">
      {text}
    </p>

    {ctaTo && (
      <Link
        to={ctaTo}
        className="mt-6 inline-flex items-center gap-2 rounded-2xl bg-indigo-600 px-5 py-3 text-sm font-bold text-white hover:bg-indigo-700 transition"
      >
        <ArrowRight className="w-4 h-4" />
        {ctaLabel}
      </Link>
    )}
  </div>
);

export default StudentDashboard;